import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChatbotApi } from './api';
import type {
  CreateChatbotExcelDto,
  CreateChatbotUIDto,
  UpdateChatbotExcelDto,
  UpdateChatbotUIDto,
  ChatbotFilters,
  UpdateSettingsDto,
  ChatbotMode,
  TaskFilters,
  UpdateStatusRequest,
  ToggleStatusRequest,
  DataPoint,
  WorkflowLinks,
} from './types';

const QUERY_KEYS = {
  all: ['chatbots'] as const,
  list: (filters?: ChatbotFilters) => [...QUERY_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...QUERY_KEYS.all, 'detail', id] as const,
  tasks: (id: string, filters?: TaskFilters, ) => [...QUERY_KEYS.all, 'tasks', id, filters] as const,
  taskGroup: (id: string, key: string) => [...QUERY_KEYS.all, 'taskGroup', id, key] as const,
  links: (code: string) => [...QUERY_KEYS.all, 'links', code] as const,
};

export const useChatbots = (filters?: ChatbotFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: () => ChatbotApi.getAll(filters),
    enabled: !!filters?.orgId,
  });
};

export const useChatbot = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(id),
    queryFn: () => ChatbotApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateChatbot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mode, data }: {
      mode: ChatbotMode;
      data: CreateChatbotExcelDto | CreateChatbotUIDto;
    }) => ChatbotApi.create(mode, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useUpdateChatbot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, mode, data }: {
      id: string;
      mode: ChatbotMode;
      data: UpdateChatbotExcelDto | UpdateChatbotUIDto;
    }) => ChatbotApi.update(id, mode, data),
    onSuccess: (chatbot) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(chatbot.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteChatbot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ChatbotApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteChatbotUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ChatbotApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useChatbotSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSettingsDto }) => ChatbotApi.updateSettings(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const usePreviewFlow = () => {
  return useMutation({
    mutationFn: (id: string) => ChatbotApi.previewFlow(id),
  });
};

export const useSyncFlow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ChatbotApi.syncFlow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const usePublishFlow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ChatbotApi.publishFlow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDeleteFlow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ChatbotApi.deleteFlow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useDataGroups = (id: string, filters?: TaskFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.tasks(id, filters),
    queryFn: () => ChatbotApi.fetchDataGroups(id, filters),
    enabled: !!id,
  });
};

// useDataGroupsForBots fetches today's entry count for each bot in parallel,
// reusing useDataGroups' own query key/fn shape so the cache is shared with
// the per-bot TasksPage (#681/#721 Phase 7c "Tasks" page, FORM tab).
export const useDataGroupsForBots = (botIds: string[], filters?: TaskFilters) => {
  return useQueries({
    queries: botIds.map((id) => ({
      queryKey: QUERY_KEYS.tasks(id, filters),
      queryFn: () => ChatbotApi.fetchDataGroups(id, filters),
      enabled: !!id,
    })),
  });
};

export const useDataGroup = (id: string, key: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.taskGroup(id, key),
    queryFn: () => ChatbotApi.fetchDataGroup(id, key),
    enabled: !!id,
  });
};

/**
 * @deprecated Use useEditTaskData instead for audit trail support
 */
export const useUpdateDataGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, request }: { id: string, request: DataPoint[] }) => ChatbotApi.updateDataGroup(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

/**
 * Edit task data with audit trail
 * Uses the workflows/command API with dataedit_ command pattern
 */
export const useEditTaskData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgId,
      botCode,
      taskId,
      repId,
      noteId,
      newValue
    }: {
      userId: string;
      orgId: string;
      botCode: string;
      taskId: string;
      repId: number;
      noteId: string;
      newValue: string;
    }) => ChatbotApi.editTaskData(userId, orgId, botCode, taskId, repId, noteId, newValue),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useChatbotTasks = (id: string, filters?: TaskFilters) => {
  return useQuery({
    queryKey: QUERY_KEYS.tasks(id, filters),
    queryFn: () => ChatbotApi.fetchTasks(id, filters),
    enabled: !!id,
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, taskId, data }: {
      id: string;
      taskId: string;
      data: UpdateStatusRequest;
    }) => ChatbotApi.updateTask(id, taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

export const useReorderChatbots = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (order: string[]) => ChatbotApi.reorder(order),
    // Returned so the mutation stays pending until the refetch lands. The caller drops its
    // optimistic order once this settles; clearing earlier would flash the stale order.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all }),
  });
};

export const useExportChatbotQrCodes = () =>
  useMutation({
    mutationFn: async (chatbotIds: string[]) => {
      const url = URL.createObjectURL(await ChatbotApi.exportQrCodes(chatbotIds));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'workflow-qr-codes.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    },
  });

export const useChatbotStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ToggleStatusRequest }) => ChatbotApi.block(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
};

// Workflow Links Hooks

/**
 * Fetch workflow links for a chatbot
 */
export const useChatbotLinks = (code: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.links(code),
    queryFn: () => ChatbotApi.getLinks(code),
    enabled: !!code,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Update workflow links for a chatbot
 */
export const useUpdateChatbotLinks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, links }: { code: string; links: WorkflowLinks }) =>
      ChatbotApi.updateLinks(code, links),
    onSuccess: (_, variables) => {
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.links(variables.code) });
    },
  });
};

/**
 * Delete a specific workflow link
 */
export const useDeleteChatbotLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, key }: { code: string; key: string }) =>
      ChatbotApi.deleteLink(code, key),
    onSuccess: (_, variables) => {
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.links(variables.code) });
    },
  });
};